/**
 * Frontend Comparison Utilities
 *
 * Helpers for multi-assessment (2-5) comparison display:
 * color palette, formatting, radar/line/bar chart data preparation.
 */

export const COMPARISON_PALETTE = [
  { name: 'CEDEC Blue', hex: '#2c5aa0', rgb: '44, 90, 160',  bg: 'bg-blue-100',  text: 'text-blue-700',  border: 'border-blue-500',  topBorder: 'border-t-blue-600' },
  { name: 'Emerald',    hex: '#059669', rgb: '5, 150, 105',   bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-500', topBorder: 'border-t-emerald-600' },
  { name: 'Amber',      hex: '#d97706', rgb: '217, 119, 6',   bg: 'bg-amber-100',  text: 'text-amber-700',  border: 'border-amber-500',  topBorder: 'border-t-amber-600' },
  { name: 'Violet',     hex: '#7c3aed', rgb: '124, 58, 237',  bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-500', topBorder: 'border-t-violet-600' },
  { name: 'Rose',       hex: '#e11d48', rgb: '225, 29, 72',   bg: 'bg-rose-100',   text: 'text-rose-700',   border: 'border-rose-500',   topBorder: 'border-t-rose-600' },
]

export const getColor = (index) => COMPARISON_PALETTE[index % COMPARISON_PALETTE.length]

// ── Formatting ──────────────────────────────────────────────

export const formatDelta = (value, precision = 1) => {
  if (value === null || value === undefined || isNaN(value)) return '--'
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(precision)}`
}

export const getDeltaColor = (delta) => {
  if (delta === null || delta === undefined || isNaN(delta)) return 'text-gray-500 bg-gray-100'
  if (delta > 0.5) return 'text-green-700 bg-green-100'
  if (delta > 0)   return 'text-green-600 bg-green-50'
  if (delta < -0.5) return 'text-red-700 bg-red-100'
  if (delta < 0)   return 'text-red-600 bg-red-50'
  return 'text-gray-600 bg-gray-100'
}

export const getDeltaArrow = (delta) => {
  if (delta === null || delta === undefined || isNaN(delta)) return '—'
  if (delta > 0.3)  return '↑↑'
  if (delta > 0)    return '↑'
  if (delta < -0.3) return '↓↓'
  if (delta < 0)    return '↓'
  return '→'
}

export const formatComparisonDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

export const shortLabel = (text, max = 15) =>
  text.length > max ? text.substring(0, max) + '…' : text

// ── Assessment labels ────────────────────────────────────────

/** "Prénom Nom" - empty string when no name is recorded. */
export const personName = (a) => {
  const first = a?.clientInfo?.firstName?.trim() || ''
  const last = a?.clientInfo?.lastName?.trim() || ''
  return `${first} ${last}`.trim()
}

/**
 * "Prénom Nom - Entreprise". Falls back to whichever part exists so that
 * several people from the same company stay distinguishable in legends.
 */
export const personCompanyLabel = (a) => {
  const name = personName(a)
  const company = a?.clientInfo?.company?.trim() || ''
  if (name && company) return `${name} - ${company}`
  return name || company || 'Évaluation'
}

export const assessmentLabel = (a) =>
  `${personCompanyLabel(a)} (${formatComparisonDate(a.clientInfo.assessmentDate)})`

/**
 * Chart.js clips anything drawn past the canvas edge, so legend entries are
 * trimmed to what the current chart width can show. Datasets keep the full
 * label, which is what the tooltips display.
 */
export const legendLabel = (text, chartWidth) =>
  shortLabel(text, chartWidth < 420 ? 20 : chartWidth < 640 ? 28 : 44)

/** Radar point labels sit in the margin, so they get even less room. */
export const radarPointLabel = (text, chartWidth) =>
  shortLabel(text, chartWidth < 420 ? 10 : chartWidth < 640 ? 14 : 18)

// ── Radar chart data (N datasets) ───────────────────────────

export const prepareRadarData = (assessments, comparison) => {
  const themes = comparison.themes.filter(t => t.matched)
  if (themes.length === 0) return null

  // Full titles: the radar point labels are trimmed to the chart width at draw time.
  const labels = themes.map(t => t.title)

  const datasets = assessments.map((a, i) => {
    const c = getColor(i)
    return {
      label: personCompanyLabel(a),
      data: themes.map(t => t.scores[i]),
      backgroundColor: `rgba(${c.rgb}, 0.15)`,
      borderColor: c.hex,
      borderWidth: 2,
      pointBackgroundColor: c.hex,
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
    }
  })

  return { labels, datasets }
}

// ── Evolution line chart data ───────────────────────────────

export const prepareEvolutionData = (assessments, comparison) => {
  const dates = assessments.map(a => a.clientInfo.assessmentDate)
  const uniqueDates = new Set(dates)
  if (uniqueDates.size < 2) return null

  const labels = assessments.map(a => formatComparisonDate(a.clientInfo.assessmentDate))

  const overallDataset = {
    label: 'Score global',
    data: comparison.overall.scores,
    borderColor: '#374151',
    backgroundColor: 'rgba(55, 65, 81, 0.1)',
    borderWidth: 3,
    pointRadius: 5,
    pointHoverRadius: 7,
    tension: 0.3,
    fill: false,
  }

  const themeDatasets = comparison.themes
    .filter(t => t.matched)
    .map((t, i) => {
      const c = getColor(i % COMPARISON_PALETTE.length)
      return {
        label: shortLabel(t.title, 25),
        data: t.scores,
        borderColor: c.hex,
        backgroundColor: `rgba(${c.rgb}, 0.1)`,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.3,
        fill: false,
        hidden: true,
      }
    })

  return { labels, datasets: [overallDataset, ...themeDatasets] }
}

// ── Grouped bar chart data (themes on X, assessments as groups) ──

export const prepareGroupedBarData = (assessments, comparison) => {
  const themes = comparison.themes.filter(t => t.matched)
  if (themes.length === 0) return null

  const labels = themes.map(t => shortLabel(t.title, 14))

  const datasets = assessments.map((a, i) => {
    const c = getColor(i)
    return {
      label: personCompanyLabel(a),
      data: themes.map(t => t.scores[i]),
      backgroundColor: `rgba(${c.rgb}, 0.7)`,
      borderColor: c.hex,
      borderWidth: 1,
      borderRadius: 4,
    }
  })

  return { labels, datasets }
}

// ── Score color helpers ─────────────────────────────────────

export const getScoreColor = (score) => {
  if (score >= 8) return 'text-green-600 bg-green-100'
  if (score >= 6) return 'text-yellow-600 bg-yellow-100'
  if (score >= 4) return 'text-orange-600 bg-orange-100'
  return 'text-red-600 bg-red-100'
}
